import React, { useEffect, useMemo, useState } from 'react';
import {
  CLASSROOM_TEAM_COLORS,
  ClassroomTeam,
  PortfolioEntry,
  attachedAiCreations,
  visibleAiCreations,
  visiblePortfolioPhotos,
  coalescePortfolioEntries,
} from '../../classroom/types';
import { ClassroomPortfolioService } from '../../classroom/ClassroomPortfolioService';
import { ResolvedSessionPlan } from '../../utils/sessionPlan';
import UploadPhotosModal from './UploadPhotosModal';
import TeacherNoteModal from './TeacherNoteModal';
import AiCreationModal from './AiCreationModal';
import PortfolioEntryCard from './PortfolioEntryCard';
import RevealFlow from './RevealFlow';
import ClassPhotoDownload from './ClassPhotoDownload';

interface PortfoliosViewProps {
  squadId: string;
  squadName: string;
  teams: ClassroomTeam[];
  entries: PortfolioEntry[];
  plan: ResolvedSessionPlan;
  liveSessionId?: string | null;
  classStatus?: 'next' | 'current';
  currentLessonId?: string;
  focusTeamId?: string | null;
  onFocusConsumed?: () => void;
}

const PortfoliosView: React.FC<PortfoliosViewProps> = ({
  squadId,
  squadName,
  teams,
  entries,
  plan,
  liveSessionId,
  classStatus,
  currentLessonId,
  focusTeamId,
  onFocusConsumed,
}) => {
  const active = useMemo(() => teams.filter((t) => !t.archived), [teams]);
  const archived = useMemo(() => teams.filter((t) => t.archived), [teams]);
  const [selectedTeamId, setSelectedTeamId] = useState<string | null>(
    focusTeamId || active[0]?.id || null
  );
  const [showArchived, setShowArchived] = useState(false);
  const [uploadOpen, setUploadOpen] = useState(false);
  const [uploadSlotIndex, setUploadSlotIndex] = useState<number | null>(null);
  const [noteEntry, setNoteEntry] = useState<PortfolioEntry | null>(null);
  const [noteBusy, setNoteBusy] = useState(false);
  const [aiEntry, setAiEntry] = useState<PortfolioEntry | null>(null);
  const [aiSourceIds, setAiSourceIds] = useState<string[]>([]);
  const [replayEntry, setReplayEntry] = useState<PortfolioEntry | null>(null);

  useEffect(() => {
    if (focusTeamId) {
      setSelectedTeamId(focusTeamId);
      onFocusConsumed?.();
    }
  }, [focusTeamId, onFocusConsumed]);

  useEffect(() => {
    if (selectedTeamId && !teams.some((t) => t.id === selectedTeamId)) {
      setSelectedTeamId(active[0]?.id || null);
    }
  }, [teams, selectedTeamId, active]);

  const selectedTeam =
    teams.find((t) => t.id === selectedTeamId) ||
    active[0] ||
    null;

  const teamEntries = useMemo(() => {
    if (!selectedTeam) return [];
    return coalescePortfolioEntries(entries.filter((e) => e.teamId === selectedTeam.id))
      .slice()
      .sort((a, b) => {
        const ai = a.slotIndex ?? 0;
        const bi = b.slotIndex ?? 0;
        if (ai !== bi) return bi - ai;
        return (b.createdAt || 0) - (a.createdAt || 0);
      });
  }, [entries, selectedTeam]);

  const liveAiEntry = aiEntry
    ? teamEntries.find((item) => item.id === aiEntry.id) || aiEntry
    : null;

  const entryCountFor = (teamId: string) =>
    coalescePortfolioEntries(entries.filter((e) => e.teamId === teamId))
      .filter((e) => visiblePortfolioPhotos(e.photos).length || attachedAiCreations(e.aiVersions).length).length;

  const coverFor = (team: ClassroomTeam) => {
    const teamEntriesList = entries.filter((e) => e.teamId === team.id);
    for (const e of teamEntriesList) {
      const gen = visibleAiCreations(e.aiVersions).find(ai => !ai.groupId || ai.status === 'revealed');
      if (gen?.posterUrl) return gen.posterUrl;
      if (gen?.url && gen.type === 'image') return gen.url;
      const photo = visiblePortfolioPhotos(e.photos)[0];
      if (photo?.url) return photo.url;
    }
    return null;
  };

  const openUpload = (slotIndex?: number | null) => {
    setUploadSlotIndex(typeof slotIndex === 'number' ? slotIndex : null);
    setUploadOpen(true);
  };

  const renderTeamCard = (team: ClassroomTeam) => {
    const colors = CLASSROOM_TEAM_COLORS[team.colorKey] || CLASSROOM_TEAM_COLORS.reef;
    const cover = coverFor(team);
    return (
      <button
        type="button"
        key={team.id}
        className={`cd-pf-card ${selectedTeam?.id === team.id ? 'is-selected' : ''}`}
        onClick={() => setSelectedTeamId(team.id)}
      >
        <div className="cd-pf-cover" style={{ background: colors.accent }}>
          {cover ? <img src={cover} alt="" /> : <span className="cd-pf-cover-fallback">✦</span>}
        </div>
        <div className="cd-pf-body">
          <h3>{team.avatarUrl && <img src={team.avatarUrl} alt="" />}{team.name}</h3>
          <p>
            {team.archived ? 'Archived · ' : ''}
            {team.memberIds.length} member{team.memberIds.length === 1 ? '' : 's'} ·{' '}
            {entryCountFor(team.id)} entries
          </p>
        </div>
      </button>
    );
  };

  return (
    <div className="cd-panel">
      {active.length === 0 && archived.length === 0 ? (
        <div className="cd-pf-empty">
          <h3>No teams yet</h3>
          <p><span aria-hidden="true">📷</span> Create teams to upload photos</p>
        </div>
      ) : (
        <>
          <div className="cd-pf-toolbar">
            <button
              type="button"
              className="cd-btn cd-btn-primary"
              onClick={() => openUpload(null)}
              disabled={active.length === 0}
            >
              📷 Upload photos
            </button>
            <ClassPhotoDownload
              squadName={squadName}
              teams={teams}
              entries={entries}
              plan={plan}
            />
            <span className="cd-pf-toolbar-hint">Choose team and lesson when uploading</span>
          </div>

          <div className="cd-pf-grid">{active.map(renderTeamCard)}</div>

          {archived.length > 0 && (
            <div className="cd-pf-archived">
              <button
                type="button"
                className="cd-btn cd-btn-ghost"
                onClick={() => setShowArchived((v) => !v)}
              >
                {showArchived ? 'Hide' : 'Show'} archived teams ({archived.length})
              </button>
              {showArchived && <div className="cd-pf-grid">{archived.map(renderTeamCard)}</div>}
            </div>
          )}

          {selectedTeam && (
            <>
              <div
                className="cd-col-label"
                style={{
                  color:
                    (CLASSROOM_TEAM_COLORS[selectedTeam.colorKey] || CLASSROOM_TEAM_COLORS.reef)
                      .accent,
                }}
              >
                {selectedTeam.name} — season
              </div>

              {teamEntries.length === 0 ? (
                <p className="cd-caption">
                  No portfolio entries yet. Upload photos and pick the lesson they belong to.
                </p>
              ) : (
                teamEntries.map((entry) => (
                  <PortfolioEntryCard
                    key={entry.id}
                    squadId={squadId}
                    squadName={squadName}
                    team={selectedTeam}
                    teams={teams}
                    entry={entry}
                    plan={plan}
                    onAddPhotos={(slotIndex) => openUpload(slotIndex)}
                    onEditNote={() => setNoteEntry(entry)}
                    onAddAiCreation={(sourcePhotoIds) => {
                      setAiSourceIds(sourcePhotoIds);
                      setAiEntry(entry);
                    }}
                    onReplayReveal={() => setReplayEntry(entry)}
                  />
                ))
              )}
            </>
          )}
        </>
      )}

      <UploadPhotosModal
        isOpen={uploadOpen}
        onClose={() => setUploadOpen(false)}
        squadId={squadId}
        teams={teams}
        plan={plan}
        initialTeamId={selectedTeamId}
        initialSlotIndex={uploadSlotIndex}
        liveSessionId={liveSessionId}
        classStatus={classStatus}
        currentLessonId={currentLessonId}
      />

      <TeacherNoteModal
        isOpen={!!noteEntry}
        initialNote={noteEntry?.teacherNote || ''}
        entryLabel={
          noteEntry
            ? `${noteEntry.slotIndex ? `Lesson ${noteEntry.slotIndex} · ` : ''}${noteEntry.levelTitle}`
            : undefined
        }
        busy={noteBusy}
        onClose={() => setNoteEntry(null)}
        onSubmit={async (note) => {
          if (!noteEntry) return;
          setNoteBusy(true);
          try {
            await ClassroomPortfolioService.setTeacherNote({
              squadId,
              entryId: noteEntry.id,
              teacherNote: note,
            });
            setNoteEntry(null);
          } catch (e) {
            console.error(e);
            void window.appAlert('Could not save note');
          } finally {
            setNoteBusy(false);
          }
        }}
      />

      <AiCreationModal
        isOpen={!!liveAiEntry}
        onClose={() => {
          setAiEntry(null);
          setAiSourceIds([]);
        }}
        squadId={squadId}
        squadName={squadName}
        teamName={selectedTeam?.name || ''}
        entry={liveAiEntry}
        initialSourcePhotoIds={aiSourceIds}
      />
      {replayEntry && <RevealFlow
        squadId={squadId}
        plan={plan}
        initialPlanIndex={Math.max(0, plan.slots.findIndex((slot) =>
          (typeof replayEntry.slotIndex === 'number' && slot.index === replayEntry.slotIndex) ||
          (slot.levelId === replayEntry.levelId && (!replayEntry.date || slot.date === replayEntry.date))
        ))}
        teams={teams}
        entries={entries}
        initialEntryId={replayEntry.id}
        replay
        onClose={() => setReplayEntry(null)}
      />}
    </div>
  );
};

export default PortfoliosView;
